import React, { useEffect, useRef, useState } from "react";

// Render a country flag from a 2/3-letter code via the flag-icons CSS
// classes (loaded once in public/index.html). Falls back to letter
// initials when no code is available — the cell never collapses.
//
// A code that EXISTS but is not a flag-icons code fell through that promise:
// the data carries FIFA codes for some entities (Tahiti = "ta"), `fi fi-ta`
// is a class the stylesheet never defines, and the cell rendered an empty
// box instead of the initials. The stylesheet is the only authority on which
// codes exist, so we ask the rendered element itself whether the class
// produced a background image — a code that resolves to none is treated
// exactly like a missing one.
export default function Flag({ code, country, size = 28 }) {
  const ref = useRef(null);
  // The code we have already found unresolvable. Keyed by code so a later
  // code gets its own check instead of inheriting this one's verdict.
  const [unresolvedCode, setUnresolvedCode] = useState(null);

  useEffect(() => {
    if (!code || unresolvedCode === code) return;
    const el = ref.current;
    if (!el) return;
    const bg = window.getComputedStyle(el).backgroundImage;
    if (!bg || bg === "none") setUnresolvedCode(code);
  }, [code, unresolvedCode]);

  if (!code || unresolvedCode === code) {
    const initials =
      (country || "?")
        .replace(/[^A-Za-z]/g, "")
        .slice(0, 2)
        .toUpperCase() || "?";
    return (
      <div
        className="inline-flex items-center justify-center bg-slate-200 text-slate-600 font-semibold rounded-sm flex-shrink-0"
        style={{
          width: size * 1.33,
          height: size,
          fontSize: Math.max(9, size * 0.42),
        }}
        aria-label={country}
      >
        {initials}
      </div>
    );
  }
  return (
    <span
      ref={ref}
      className={`fi fi-${code} inline-block bg-cover bg-center rounded-sm flex-shrink-0`}
      style={{
        width: size * 1.33,
        height: size,
        boxShadow: "inset 0 0 0 0.5px rgba(0,0,0,0.08)",
      }}
      role="img"
      aria-label={country}
    />
  );
}
